import React, { useState } from 'react';
import i18n from 'i18n-js';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Layout from 'components/Layout';
import Title from 'components/Title';
import { Chart } from 'components/Charts';
import { fetchCharts } from 'services/api/charts';
import { cubejsApi } from 'services/cubejs';
import { CubeProvider } from '@cubejs-client/react';
import { DateRangePicker } from 'components/UI';
import moment from 'moment';

const labelSx = {
  fontSize: '0.625rem',
  lineHeight: 1.4,
  color: 'secondary.main',
  transform: 'translate(0, 4px) scale(1)',
};

const selectSx = {
  width: '100%',
  padding: '0 0.5625rem',
  fontSize: '0.875rem',
};

const ChartList = () => {
  //NO BE Call, this is a mocked API
  const charList = fetchCharts();
  const [charts, setCharts] = useState(charList);
  const [dateRange, setDateRange] = useState('from 1 year ago to now');
  const [granularity, setGranularity] = useState('month');
  const [dates, setDates] = useState({
    startDate: moment().subtract(12, 'months'),
    endDate: moment.now(),
  });

  const cubejsApiData = cubejsApi('reporting');

  const DATE_RANGES = [
    { value: 'Range' },
    { value: 'none', title: 'All' },
    { value: 'from 1 year ago to now', title: 'Last 12 months' },
    { value: 'from 6 months ago to now', title: 'Last 6 months' },
    // Both options are disabled as this will produce a single value not suitable for charts
    // { value: 'Today' },
    // { value: 'Yesterday' },
    { value: 'This week' },
    { value: 'This month' },
    { value: 'This quarter' },
    { value: 'This year' },
    { value: 'Last 7 days' },
    { value: 'Last 30 days' },
    { value: 'Last week' },
    { value: 'Last month' },
    { value: 'Last quarter' },
    { value: 'Last year' },
  ];

  const GRANULARITIES = [
    { value: 'day', title: i18n.t('reports.granularities.day') },
    { value: 'month', title: i18n.t('reports.granularities.month') },
    { value: 'week', title: i18n.t('reports.granularities.week') },
    { value: 'year', title: i18n.t('reports.granularities.year') },
  ];

  const rangeToDatesMap = {
    'from 1 year ago to now': [moment().subtract(12, 'months'), moment.now(), 'month'],
    'from 6 months ago to now': [moment().subtract(6, 'months'), moment.now(), 'month'],
    'This week': [moment().startOf('isoWeek'), moment().endOf('isoWeek'), 'day'],
    'This month': [moment().startOf('month'), moment().endOf('month'), 'day'],
    'This quarter': [moment().startOf('quarter'), moment().endOf('quarter'), 'day'],
    'This year': [moment().startOf('year'), moment().endOf('year'), 'month'],
    'Last 7 days': [moment().subtract(7, 'days'), moment().subtract(1, 'days'), 'day'],
    'Last 30 days': [moment().subtract(30, 'days'), moment().subtract(1, 'days'), 'day'],
    'Last week': [
      moment().subtract(1, 'week').startOf('isoWeek'),
      moment().subtract(1, 'week').endOf('isoWeek'),
      'day',
    ],
    'Last month': [
      moment().subtract(1, 'month').startOf('month'),
      moment().subtract(1, 'month').endOf('month'),
      'day',
    ],
    'Last quarter': [
      moment().subtract(1, 'quarter').startOf('quarter'),
      moment().subtract(1, 'quarter').endOf('quarter'),
      'month',
    ],
    'Last year': [
      moment().subtract(1, 'year').startOf('year'),
      moment().subtract(1, 'year').endOf('year'),
      'month',
    ],
  };

  const handleUpdateDateRange = (event) => {
    const newDateRange = event?.target?.value;
    setDateRange(newDateRange);
    if (newDateRange !== 'Range') {
      const [startDate, endDate, granularity] = rangeToDatesMap[newDateRange] || ['', '', 'day'];
      setDates({ startDate, endDate });
      setGranularity(granularity);

      setCharts((prevState) => {
        const updatedCharts = [...prevState].map((chart) => ({
          ...chart,
          query: {
            ...chart.query,
            timeDimensions: [
              {
                ...chart.query.timeDimensions[0],
                ...(newDateRange !== 'none'
                  ? { dateRange: newDateRange, granularity }
                  : { dateRange: undefined, granularity }),
              },
            ],
          },
        }));
        return updatedCharts;
      });
    }
  };

  const handleUpdateGranularity = (event) => {
    const newGranularity = event?.target?.value;
    setGranularity(newGranularity);
    setCharts((prevState) => {
      const updatedCharts = [...prevState].map((chart) => ({
        ...chart,
        query: {
          ...chart.query,
          timeDimensions: [{ ...chart.query.timeDimensions[0], granularity: newGranularity }],
        },
      }));
      return updatedCharts;
    });
  };

  const handleUpdateDates = (dates) => {
    const { startDate, endDate } = dates;
    setDates((prevState) => ({
      ...prevState,
      startDate,
      endDate,
    }));
    setDateRange('Range');
    if (startDate && endDate) {
      //As you select a date range on value the granularity is best display on days
      const newGranularity = 'day';
      setGranularity(newGranularity);
      setCharts((prevState) => {
        const updatedCharts = [...prevState].map((chart) => ({
          ...chart,
          query: {
            ...chart.query,
            timeDimensions: [
              {
                ...chart.query.timeDimensions[0],
                dateRange: [startDate.format('YYYY-MM-DD'), endDate.format('YYYY-MM-DD')],
                granularity: newGranularity,
              },
            ],
          },
        }));
        return updatedCharts;
      });
    }
  };

  return (
    <CubeProvider cubejsApi={cubejsApiData}>
      <Layout title={i18n.t('charts.title')}>
        <Container sx={{ minWidth: '100%', margin: 0 }}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <Grid
                container
                spacing={2}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Grid>
                  <Title title={i18n.t('charts.title')} />
                </Grid>
              </Grid>
            </Grid>
            <Grid size={12}>
              <Grid
                container
                spacing={2}
                direction="row"
                alignItems="flex-end"
                sx={{ marginBottom: '0.5rem' }}
              >
                <Grid>
                  <FormControl fullWidth variant="standard">
                    <InputLabel sx={labelSx} id="rangeLabel">
                      {i18n.t('charts.quick_range')}
                    </InputLabel>
                    <Select
                      labelId="rangeLabel"
                      id="range"
                      value={dateRange}
                      onChange={(event) => handleUpdateDateRange(event)}
                      sx={{ ...selectSx, minWidth: '10rem' }}
                      label={i18n.t('charts.quick_range')}
                      MenuProps={{
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left',
                        },
                      }}
                    >
                      {DATE_RANGES.map((range) => (
                        <MenuItem key={range.value} value={range.value}>
                          {range.title || range.value}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid>
                  <DateRangePicker
                    showClearDates
                    onDatesChange={handleUpdateDates}
                    label={i18n.t('charts.date_range')}
                    styles={{ width: '100%' }}
                    startDate={dates.startDate ? moment(dates.startDate) : undefined}
                    endDate={dates.endDate ? moment(dates.endDate) : undefined}
                  />
                </Grid>
                <Grid>
                  <FormControl fullWidth variant="standard">
                    <InputLabel sx={labelSx} id="granularitiesLabel">
                      {i18n.t('charts.grouped')}
                    </InputLabel>
                    <Select
                      labelId="granularitiesLabel"
                      id="granularities"
                      defaultValue=" "
                      value={granularity}
                      onChange={(event) => handleUpdateGranularity(event)}
                      sx={{ ...selectSx, minWidth: '7rem' }}
                      MenuProps={{
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left',
                        },
                      }}
                    >
                      {GRANULARITIES.map((granularity) => (
                        <MenuItem key={granularity.value} value={granularity.value}>
                          {granularity.title || granularity.value}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
            <Grid
              size={12}
              sx={{
                width: '100%',
                height: '100%',
                display: 'grid',
                gridTemplateColumns: 'repeat(12, 1fr)',
                gridTemplateRows: 'repeat(2, 1fr)',
                gap: '1.25rem 1.25rem',
              }}
            >
              {charts.map((chartProps) => (
                <Chart
                  key={chartProps.id}
                  {...chartProps}
                  granularity={granularity}
                  dates={dates}
                />
              ))}
            </Grid>
          </Grid>
        </Container>
      </Layout>
    </CubeProvider>
  );
};

export { ChartList };
